import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { Paginator } from "primereact/paginator";
import * as Yup from "yup";
import {
  getReimbursementList,
  postReimbursementAddUpdate,
  postReimbursementDelete,
} from "./Services/PayrollService";
import { getEmployeeList } from "../EmployeeList/Service/employeeService";
import { GetMasterTypeData } from "../../../Services/Global-Services/GlobalServices";
import { TostMsg } from "../../../App";

// ---------- helpers ----------
const rup = (n) =>
  n === null || n === undefined || n === ""
    ? "—"
    : "₹" + Number(n).toLocaleString("en-IN");

// API wants the numeric employee id; prefer `EmpId`, fall back to `Id`.
const empKey = (e) => e?.EmpId ?? e?.Id;
const findEmployee = (list, ref) =>
  list.find(
    (e) =>
      String(empKey(e)) === String(ref) ||
      String(e.Id) === String(ref) ||
      String(e.EmploymentId) === String(ref),
  );

const typeBadge = (t) =>
  ({
    Meal: "badge-meal",
    Fuel: "badge-fuel",
    Mobile: "badge-mobile",
    Internet: "badge-internet",
    LTA: "badge-lta",
  })[t] || "badge-other";

const statusBadge = (name) => {
  const n = String(name).toLowerCase();
  if (n === "approved" || n === "paid") return "badge-paid";
  if (n === "pending" || n === "open") return "badge-pending";
  return "badge-locked";
};

const EMPTY_FORM = {
  EmpId: "",
  ReimbursementTypeId: "",
  ReimbursementStatusId: "",
  Amount: "",
  ForMonth: "",
  Desc: "",
};

const FieldError = ({ message }) =>
  message ? (
    <small style={{ color: "var(--danger)", display: "block", marginTop: "4px" }}>
      {message}
    </small>
  ) : null;

const schema = Yup.object().shape({
  EmpId: Yup.string().required("Employee is required"),
  ReimbursementTypeId: Yup.string().required("Reimbursement type is required"),
  ReimbursementStatusId: Yup.string().required("Status is required"),
  Amount: Yup.number()
    .typeError("Amount must be a number")
    .required("Amount is required")
    .positive("Amount must be greater than 0")
    .max(10000000, "Amount cannot exceed ₹1,00,00,000")
    .test(
      "two-decimals",
      "Use at most 2 decimal places",
      (v) => v === undefined || Math.round(v * 100) / 100 === v,
    ),
  ForMonth: Yup.string().required("Month is required"),
  Desc: Yup.string().max(250, "Description cannot exceed 250 characters"),
});

const ReimbursementManagement = () => {
  const toaster = useContext(TostMsg);
  const userDetails = useMemo(
    () => JSON.parse(localStorage.getItem("User") || "{}"),
    [],
  );

  // lookups
  const [statusList, setStatusList] = useState([]);
  const [typeList, setTypeList] = useState([]);
  const [formonthList, setFormonthList] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);

  // list
  const [reimbs, setReimbs] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);

  // filters
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const searchTimeout = useRef(null);

  // modals / form
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editReimb, setEditReimb] = useState(null);
  const [deleteReimb, setDeleteReimb] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // ---------- loading ----------
  useEffect(() => {
    loadMasterData();
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMasterData = async () => {
    try {
      const base = { allRecords: true, organisationId: userDetails?.OrgId };
      const [statusRes, typeRes, monthRes] = await Promise.all([
        GetMasterTypeData({ ...base, masterType: "ReimbusementStatus" }),
        GetMasterTypeData({ ...base, masterType: "ReimbursementTypes" }),
        GetMasterTypeData({ ...base, masterType: "formonth" }),
      ]);
      setStatusList(statusRes?.Data || []);
      setTypeList(typeRes?.Data || []);
      setFormonthList(monthRes?.Data || []);
    } catch (error) {
      console.error("Error loading master data:", error);
      toaster?.current?.error("Could not load reimbursement master data.");
    }
  };

  const loadEmployees = async () => {
    try {
      const res = await getEmployeeList({
        OrganisationId: userDetails?.OrgId,
        LoggedInEmployeeId: userDetails?.EpmId,
        IsDirectReportOrHierarchy: 0,
        EmployeeId: 0,
        SearchText: "",
        FollowersOnly: 0,
        FollowingsOnly: 0,
        IsSuggestedOnly: 0,
        IsExEmployeesAlso: 0,
        pageNumber: 1,
        pageSize: 10000,
      });
      setEmployeeList(res?.Data || []);
    } catch (error) {
      console.error("Error fetching employee list:", error);
      toaster?.current?.error("Could not load employee list.");
    }
  };

  const fetchReimbursements = async (page = 1, size = rows) => {
    setIsLoading(true);
    try {
      const res = await getReimbursementList({
        ReimbursementId: 0,
        LoggedInEmpId: 0,
        OrgId: userDetails?.OrgId,
        GUID: "",
        EmpId: 0,
        PageNumber: page,
        PageSize: size,
        Search: searchQuery || "",
        ReimbursementStatusId: filterStatus || "",
        ReimbursementTypeId: filterType || "",
      });
      const items = res?.Data?.ReimbursementItems || [];
      setReimbs(items);
      setTotalRecords(
        res?.Data?.RecordCount ??
          res?.Data?.TotalRecords ??
          res?.Data?.TotalCount ??
          items.length,
      );
    } catch (error) {
      console.error("Error fetching reimbursement list:", error);
      toaster?.current?.error("Could not load claims. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setFirst(0);
      fetchReimbursements(1, rows);
    }, 500);
    return () => clearTimeout(searchTimeout.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filterType, filterStatus]);

  const currentPage = () => Math.floor(first / rows) + 1;

  const onPageChange = (event) => {
    setFirst(event.first);
    setRows(event.rows);
    fetchReimbursements(event.first / event.rows + 1, event.rows);
  };

  // ---------- validation ----------
  const validateField = async (name, data = formData) => {
    try {
      await schema.validateAt(name, data);
      setFormErrors((p) => ({ ...p, [name]: "" }));
    } catch (err) {
      setFormErrors((p) => ({ ...p, [name]: err.message }));
    }
  };

  const validateForm = async () => {
    try {
      await schema.validate(formData, { abortEarly: false });
      setFormErrors({});
      return true;
    } catch (err) {
      const errors = {};
      err.inner.forEach((e) => {
        if (!errors[e.path]) errors[e.path] = e.message;
      });
      setFormErrors(errors);
      return false;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const next = { ...formData, [name]: value };
    setFormData(next);
    if (formErrors[name]) validateField(name, next);
  };

  const handleBlur = (e) => validateField(e.target.name);

  const applyServerErrors = (res) => {
    const serverErrors = res?.Errors || res?.errors;
    if (!serverErrors || typeof serverErrors !== "object") return false;
    const entries = Array.isArray(serverErrors)
      ? serverErrors.map((e) => [e.Field || e.field || e.PropertyName, e.Message || e.message])
      : Object.entries(serverErrors).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v]);
    const mapped = {};
    entries.forEach(([key, msg]) => {
      const field = Object.keys(EMPTY_FORM).find(
        (f) => f.toLowerCase() === String(key || "").toLowerCase(),
      );
      if (field && msg) mapped[field] = msg;
    });
    if (!Object.keys(mapped).length) return false;
    setFormErrors((p) => ({ ...p, ...mapped }));
    return true;
  };

  // ---------- open / close ----------
  const defaultStatusId = () => {
    const pending = statusList.find((s) =>
      ["pending", "open"].includes(String(s.Name).toLowerCase()),
    );
    return String((pending || statusList[0])?.Id ?? "");
  };

  const openAdd = () => {
    setEditReimb(null);
    setFormErrors({});
    setFormData({ ...EMPTY_FORM, ReimbursementStatusId: defaultStatusId() });
    setShowModal(true);
  };

  const openEdit = (r) => {
    const type =
      typeList.find((t) => String(t.Id) === String(r.ReimbursementTypeId)) ||
      typeList.find((t) => t.Name === r.ReimbursementTypeName);
    const status =
      statusList.find((s) => String(s.Id) === String(r.ReimbursementStatusId)) ||
      statusList.find((s) => s.Name === r.ReimbursementStatusName);
    const emp = findEmployee(employeeList, r.EmpId);

    setEditReimb(r);
    setFormErrors({});
    setFormData({
      EmpId: emp ? String(empKey(emp)) : "",
      ReimbursementTypeId: type ? String(type.Id) : "",
      ReimbursementStatusId: status ? String(status.Id) : defaultStatusId(),
      Amount: r.Amount ?? "",
      ForMonth: r.ForMonth || "",
      Desc: r.Desc || "",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (!isSaving) setShowModal(false);
  };

  // ---------- CRUD ----------
  const saveReimb = async () => {
    if (isSaving) return;
    if (!(await validateForm())) return;

    const f = formData;
    const params = {
      Id: editReimb?.Id || 0,
      OrgId: userDetails?.OrgId || 0,
      EmpId: Number(f.EmpId),
      ReimbursementTypeId: Number(f.ReimbursementTypeId),
      ReimbursementStatusId: Number(f.ReimbursementStatusId),
      Amount: Number(f.Amount),
      Desc: f.Desc.trim(),
      ForMonth: f.ForMonth,
    };

    setIsSaving(true);
    try {
      const res = await postReimbursementAddUpdate(params);
      if (res?.StatusCode === 2000) {
        toaster?.current?.success(
          `Reimbursement ${editReimb ? "updated" : "added"} successfully`,
        );
        setShowModal(false);
        if (editReimb) {
          fetchReimbursements(currentPage(), rows);
        } else {
          setFirst(0);
          fetchReimbursements(1, rows);
        }
      } else {
        const shown = applyServerErrors(res);
        toaster?.current?.error(
          res?.Message ||
            (shown ? "Please fix the highlighted fields." : "Error saving reimbursement. Please try again."),
        );
      }
    } catch (error) {
      console.error("Error saving reimbursement:", error);
      toaster?.current?.error("Error saving reimbursement. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const askDelete = (r) => {
    setDeleteReimb(r);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteReimb || isDeleting) return;
    setIsDeleting(true);
    try {
      const res = await postReimbursementDelete({ RequestId: deleteReimb.Id });
      if (res?.StatusCode === 2000) {
        toaster?.current?.success("Reimbursement deleted successfully");
        setShowDeleteModal(false);
        setDeleteReimb(null);
        let page = currentPage();
        if (reimbs.length === 1 && page > 1) {
          page -= 1;
          setFirst((page - 1) * rows);
        }
        fetchReimbursements(page, rows);
      } else {
        toaster?.current?.error(
          res?.Message || "Error deleting reimbursement. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error deleting reimbursement:", error);
      toaster?.current?.error("Error deleting reimbursement. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  // ---------- export (current page) ----------
  const handleExport = () => {
    if (!reimbs.length) {
      toaster?.current?.error("No data to export.");
      return;
    }
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const headers = ["Employee", "Employee ID", "Type", "Month", "Amount", "Status", "Description"];
    const body = reimbs.map((r) => [
      r.EmployeeName, r.EmpId, r.ReimbursementTypeName, r.ForMonth,
      r.Amount, r.ReimbursementStatusName, r.Desc,
    ]);
    const csv = [headers, ...body].map((row) => row.map(esc).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "reimbursements.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ---------- page totals (current page only) ----------
  const totals = useMemo(() => {
    const sumBy = (name) =>
      reimbs
        .filter((r) => r.ReimbursementTypeName === name)
        .reduce((s, r) => s + (r.Amount || 0), 0);
    return {
      all: reimbs.reduce((s, r) => s + (r.Amount || 0), 0),
      meal: sumBy("Meal"),
      fuel: sumBy("Fuel"),
      lta: sumBy("LTA"),
    };
  }, [reimbs]);

  // ---------- render ----------
  return (
    <div className="container-fluid">
      <div className="form-head d-flex flex-wrap align-items-center page-head mb-0">
        <div className="me-auto">
          <h2 className="font-w600 mb-0">Reimbursement Management</h2>
          <p>
            Employee reimbursement claims — Meal, Fuel, Mobile, Internet, LTA,
            Books &amp; Education. Approved claims flow into payroll Step 6.
          </p>
        </div>
        <div className="rightside-tabs">
          <div className="topbar-actions">
            <button className="btn-og btn-og-secondary btn-og-sm" onClick={handleExport}>
              <i className="pi pi-file-excel"></i> Export
            </button>
            <button className="btn-og theme-color btn-og-sm" onClick={openAdd}>
              <i className="pi pi-plus" style={{ fontSize: "12px" }}></i> Add Claim
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        {/* SUMMARY (calculated from the rows on this page) */}
        <div className="rem-summary-grid">
          {[
            ["Total Claims", totalRecords, false],
            ["This Month Total", totals.all, true],
            ["Meal", totals.meal, true],
            ["Fuel / Travel", totals.fuel, true],
            ["LTA Claims", totals.lta, true],
          ].map(([label, value, money]) => (
            <div className="rem-summary-card" key={label}>
              <div className="rem-summary-label">{label}</div>
              <div className="rem-summary-value">{money ? rup(value) : value}</div>
            </div>
          ))}
        </div>

        {/* TABLE */}
        <div className="card" style={{ overflow: "hidden" }}>
          <div className="toolbar filter-row pt-2">
            <input
              type="text"
              className="form-control"
              placeholder="Search employee…"
              style={{ width: "200px" }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="toolbar-sep"></div>
            <select
              className="fs-select"
              style={{ width: "160px" }}
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">All Types</option>
              {typeList.map((t) => (
                <option key={t.Id} value={t.Id}>{t.Name}</option>
              ))}
            </select>
            <select
              className="fs-select"
              style={{ width: "140px" }}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All Status</option>
              {statusList.map((s) => (
                <option key={s.Id} value={s.Id}>{s.Name}</option>
              ))}
            </select>
            <div className="toolbar-spacer"></div>
            <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>
              {totalRecords} claims
            </span>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table className="mgmt-tbl">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Month</th>
                  <th className="tr">Amount</th>
                  <th style={{ textAlign: "center" }}>Status</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan="7" style={{ textAlign: "center", padding: "24px" }}>
                      Loading claims…
                    </td>
                  </tr>
                )}
                {!isLoading && reimbs.length === 0 && (
                  <tr>
                    <td colSpan="7" style={{ textAlign: "center", padding: "32px", color: "var(--text-muted)" }}>
                      No records found. Change the filters or add a claim.
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  reimbs.map((r) => (
                    <tr key={r.Id}>
                      <td>
                        <span style={{ fontWeight: 600 }}>{r.EmployeeName}</span>
                        <br />
                        <code style={{ fontSize: "10px", color: "var(--brand)" }}>
                          {r.EmployeeCode || r.EmpId}
                        </code>
                      </td>
                      <td>
                        <span className={`badge ${typeBadge(r.ReimbursementTypeName)}`} style={{ fontSize: "10.5px" }}>
                          {r.ReimbursementTypeName}
                        </span>
                      </td>
                      <td style={{ fontSize: "12px", color: "var(--text-muted)" }}>{r.Desc || "—"}</td>
                      <td style={{ fontSize: "12px", color: "var(--text-muted)" }}>{r.ForMonth}</td>
                      <td className="tr" style={{ fontWeight: 700 }}>{rup(r.Amount)}</td>
                      <td style={{ textAlign: "center" }}>
                        {r.ReimbursementStatusName ? (
                          <span className={`badge ${statusBadge(r.ReimbursementStatusName)}`}>
                            {r.ReimbursementStatusName}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
                          <button className="btn btn-light btn-sm" onClick={() => openEdit(r)} title="Edit">
                            <i className="fa-solid fa-pen"></i>
                          </button>
                          <button
                            className="btn btn-light btn-sm"
                            onClick={() => askDelete(r)}
                            title="Delete"
                            style={{ color: "var(--danger)" }}
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="4">PAGE TOTAL</td>
                  <td className="tr" style={{ fontWeight: 700 }}>{rup(totals.all)}</td>
                  <td colSpan="2">—</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <Paginator
            first={first}
            rows={rows}
            totalRecords={totalRecords}
            rowsPerPageOptions={[10, 20, 50, 100]}
            onPageChange={onPageChange}
            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
          />
        </div>
      </div>

      {/* ADD / EDIT MODAL */}
      {showModal && (
        <div className="mo open" onClick={closeModal}>
          <div className="mb" onClick={(e) => e.stopPropagation()}>
            <div className="mh">
              <div className="icon-div">
                <i className="pi pi-receipt" style={{ fontSize: "13px" }}></i>
              </div>
              <h6 style={{ margin: 0, fontSize: "14px", fontWeight: 700 }}>
                {editReimb ? "Edit Claim" : "Add Reimbursement Claim"}
              </h6>
              <button
                style={{ marginLeft: "auto", background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#888", lineHeight: 1 }}
                onClick={closeModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="mbody">
              <div className="fsec">Employee &amp; Claim Details</div>
              <div className="fg2">
                <div>
                  <label className="form-label">
                    Employee <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <select
                    className="form-select w-100"
                    name="EmpId"
                    value={formData.EmpId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={formErrors.EmpId ? { borderColor: "var(--danger)" } : {}}
                  >
                    <option value="">Select…</option>
                    {employeeList.map((emp) => (
                      <option key={emp.Id} value={empKey(emp)}>
                        {emp.FullName} ({emp.EmploymentId})
                      </option>
                    ))}
                  </select>
                  <FieldError message={formErrors.EmpId} />
                </div>

                <div>
                  <label className="form-label">
                    Reimbursement Type <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <select
                    className="form-select w-100"
                    name="ReimbursementTypeId"
                    value={formData.ReimbursementTypeId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={formErrors.ReimbursementTypeId ? { borderColor: "var(--danger)" } : {}}
                  >
                    <option value="">Select…</option>
                    {typeList.map((t) => (
                      <option key={t.Id} value={t.Id}>{t.Name}</option>
                    ))}
                  </select>
                  <FieldError message={formErrors.ReimbursementTypeId} />
                </div>

                <div>
                  <label className="form-label">
                    Amount (₹) <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control w-100"
                    name="Amount"
                    min="1"
                    step="0.01"
                    placeholder="0"
                    value={formData.Amount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={formErrors.Amount ? { borderColor: "var(--danger)" } : {}}
                  />
                  <FieldError message={formErrors.Amount} />
                </div>

                <div>
                  <label className="form-label">
                    For Month <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <select
                    className="form-select w-100"
                    name="ForMonth"
                    value={formData.ForMonth}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={formErrors.ForMonth ? { borderColor: "var(--danger)" } : {}}
                  >
                    <option value="">Select…</option>
                    {formonthList.map((m) => (
                      <option key={m.Id} value={m.Name}>{m.Name}</option>
                    ))}
                  </select>
                  <FieldError message={formErrors.ForMonth} />
                </div>

                <div>
                  <label className="form-label">
                    Status <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <select
                    className="form-select w-100"
                    name="ReimbursementStatusId"
                    value={formData.ReimbursementStatusId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={formErrors.ReimbursementStatusId ? { borderColor: "var(--danger)" } : {}}
                  >
                    <option value="">Select…</option>
                    {statusList.map((s) => (
                      <option key={s.Id} value={s.Id}>{s.Name}</option>
                    ))}
                  </select>
                  <FieldError message={formErrors.ReimbursementStatusId} />
                </div>

                <div style={{ gridColumn: "1 / -1" }}>
                  <label className="form-label">Description / Notes</label>
                  <input
                    type="text"
                    className="form-control w-100"
                    name="Desc"
                    maxLength="250"
                    placeholder="e.g. Monthly meal, client travel, LTA claim…"
                    value={formData.Desc}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <FieldError message={formErrors.Desc} />
                </div>
              </div>
            </div>

            <div className="mfoot">
              <button className="btn btn-light" onClick={closeModal} disabled={isSaving}>
                Cancel
              </button>
              <button className="btn btn-brand" onClick={saveReimb} disabled={isSaving}>
                <i className="pi pi-check"></i>{" "}
                {isSaving ? "Saving…" : editReimb ? "Save Changes" : "Add Claim"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM */}
      {showDeleteModal && deleteReimb && (
        <div className="mo open" onClick={() => !isDeleting && setShowDeleteModal(false)}>
          <div
            style={{ background: "#fff", borderRadius: "12px", width: "400px", padding: "24px", boxShadow: "0 24px 64px rgba(0,0,0,.18)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
              <div style={{ width: "34px", height: "34px", background: "#fee2e2", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--danger)" }}>
                <i className="pi pi-trash"></i>
              </div>
              <h6 style={{ margin: 0, fontWeight: 700 }}>Delete Claim</h6>
            </div>
            <p style={{ fontSize: "13px", color: "var(--text-mid)", margin: "0 0 18px" }}>
              Delete reimbursement claim for {deleteReimb.EmployeeName} (
              {deleteReimb.ReimbursementTypeName}, {rup(deleteReimb.Amount)})?
            </p>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button className="btn btn-light" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>
                Cancel
              </button>
              <button
                className="btn btn-brand"
                style={{ background: "var(--danger)", borderColor: "var(--danger)" }}
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReimbursementManagement;
